import { Checkbox } from '@/components/ui/checkbox';
import type { CustomField } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';

// ============================================
// KANBAN CARD FIELDS PICKER
// ============================================
//
// Inline UI used inside the view-edit dialog. Lets the user decide which
// custom fields appear as chips on every card in the active Kanban view.
// Only fields that have a meaningful single-value rendering (SELECT,
// MULTI_SELECT, TEXT) are listed — PERSON/DATE/CHECKBOX would clutter the
// card and are already surfaced via their own UI on each card.
//
// Visual pattern follows the table view's "visibleColumns" picker so users
// who already customized a table view get a familiar mental model.

interface KanbanCardFieldsPickerProps {
    customFields: CustomField[];
    selectedFieldIds: string[];
    onChange: (next: string[]) => void;
}

const RENDERABLE_FIELD_TYPES = new Set(['TEXT', 'NUMBER', 'SELECT', 'MULTI_SELECT', 'CHECKBOX', 'URL', 'EMAIL']);

export function KanbanCardFieldsPicker({
    customFields,
    selectedFieldIds,
    onChange,
}: KanbanCardFieldsPickerProps) {
    const { t } = useI18n();

    const renderable = customFields.filter((f) => RENDERABLE_FIELD_TYPES.has(f.type));

    if (renderable.length === 0) {
        return (
            <p className="text-xs text-foreground-muted">
                {t('kanban.noCardFieldsAvailable')}
            </p>
        );
    }

    const toggle = (fieldId: string) => {
        const next = selectedFieldIds.includes(fieldId)
            ? selectedFieldIds.filter((id) => id !== fieldId)
            : [...selectedFieldIds, fieldId];
        onChange(next);
    };

    return (
        <div className="space-y-2">
            <p className="text-xs text-foreground-muted">{t('kanban.cardFieldsHint')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto rounded-md border border-white/10 p-2">
                {renderable.map((field) => {
                    const checked = selectedFieldIds.includes(field.id);
                    return (
                        <label
                            key={field.id}
                            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-hover cursor-pointer"
                        >
                            <Checkbox checked={checked} onCheckedChange={() => toggle(field.id)} />
                            <span className="text-sm truncate">{field.name}</span>
                            <span className="ml-auto text-[10px] uppercase tracking-wide text-foreground-muted">
                                {field.type}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
